/**
 * Space-Entity Store
 *
 * Per-space LRU cache of entities, populated via the same two-stage fetch
 * the agent runtime uses (entityService.getEntitiesForSpace). Replaces the
 * dataspace-wide bulk-load of useEntityStore for space-scoped reads.
 *
 *   - Memory cache: up to MAX_CACHED_SPACES spaces (LRU).
 *   - Source-of-truth for sidebar / Cmd+K / app stores once flag is on.
 *   - Cleared on org switch.
 *
 * IDB persistence is added in Phase 2 (gui/src/services/idb/spaceEntityIDB.ts).
 *
 * See plans/entity_loading_refactor_8ae93a18.plan.md.
 */

import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { getOrgEdgesSnapshot, waitForOrgSync } from './orgYDocStore';
import { useDataspaceTreeStore } from './dataspaceTreeStore';
import { useDataspaceContextStore, waitForDataspaceContext } from './dataspaceContextStore';
import { registerScopeAdapter, resolveScope, notifyChange as notifyRegistryChange } from './entityRegistry';
import { readSnapshot, writeSnapshot, patchEntity as idbPatchEntity, deleteSnapshot } from '../services/idb/spaceEntityIDB';
import { createFileLogger } from '../utils/logger';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('SPACE_ENTITY_STORE');

const MAX_CACHED_SPACES = 8;
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export interface SpaceCacheEntry {
  spaceId: string;
  entities: BaseEntityType[];
  entityById: Map<string, BaseEntityType>;
  fetchedAt: number;
  source: 'idb' | 'rest';
}

interface SpaceEntityState {
  caches: Map<string, SpaceCacheEntry>;
  loading: Set<string>;
  inflight: Map<string, Promise<void>>;
  bumpVersion: number;
}

interface SpaceEntityActions {
  /** Load entities for `spaceId`. Idempotent across concurrent callers. */
  initializeSpace: (spaceId: string, opts?: { force?: boolean }) => Promise<void>;
  /** Synchronous read of the cached entities for a space. Returns [] if not loaded. */
  getEntities: (spaceId: string) => BaseEntityType[];
  /** Synchronous read of a single entity from the space cache. */
  getEntity: (spaceId: string, entityId: string) => BaseEntityType | undefined;
  /** Patch an entity in the in-memory cache (e.g. when a Y.Doc node changes). */
  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => void;
  /** Drop a space from memory. */
  evictSpace: (spaceId: string) => void;
  /** Drop everything (called on org switch). */
  clearAll: () => void;
}

export type SpaceEntityStore = SpaceEntityState & SpaceEntityActions;

function readEdgesSnapshot(): Array<{ parentId: string | null; childId: string }> {
  const orgEdges = getOrgEdgesSnapshot();
  if (orgEdges.length > 0) {
    return orgEdges.map((e) => ({ parentId: e.parentId, childId: e.childId }));
  }
  const treeNodes = useDataspaceTreeStore.getState().nodesById;
  const result: Array<{ parentId: string | null; childId: string }> = [];
  for (const [, node] of Object.entries(treeNodes)) {
    if (!node || !node.id) continue;
    result.push({ parentId: (node as any).parentId ?? null, childId: node.id });
  }
  return result;
}

function buildEntry(spaceId: string, entities: BaseEntityType[], source: SpaceCacheEntry['source']): SpaceCacheEntry {
  const entityById = new Map<string, BaseEntityType>();
  for (const e of entities) {
    if (e._id) entityById.set(e._id, e);
    if (e.id && e.id !== e._id) entityById.set(e.id, e);
  }
  return {
    spaceId,
    entities,
    entityById,
    fetchedAt: Date.now(),
    source,
  };
}

function lruInsert(
  caches: Map<string, SpaceCacheEntry>,
  entry: SpaceCacheEntry,
): Map<string, SpaceCacheEntry> {
  const next = new Map(caches);
  next.delete(entry.spaceId);
  next.set(entry.spaceId, entry);
  while (next.size > MAX_CACHED_SPACES) {
    const oldestKey = next.keys().next().value as string | undefined;
    if (!oldestKey) break;
    next.delete(oldestKey);
  }
  return next;
}

export const useSpaceEntityStore = create<SpaceEntityStore>((set, get) => ({
  caches: new Map(),
  loading: new Set(),
  inflight: new Map(),
  bumpVersion: 0,

  initializeSpace: async (spaceId: string, opts: { force?: boolean } = {}) => {
    if (!spaceId) return;

    const existingInflight = get().inflight.get(spaceId);
    if (existingInflight && !opts.force) {
      return existingInflight;
    }

    const promise = (async () => {
      const t0 = performance.now();

      try {
        set((s) => {
          const loading = new Set(s.loading);
          loading.add(spaceId);
          return { loading };
        });

        // Cold-reload guard: `dataspaceContextStore.currentContext` is not
        // persisted, so on the first render after a hard reload the orgId is
        // null until App.tsx finishes its `/user/context` fetch. If we
        // captured that null value and then the cross-org guard below tripped
        // the moment the real orgId landed, the REST result would be
        // silently dropped and the space cache would stay empty. Waiting
        // here resolves immediately when context is already populated.
        let currentDataspaceId =
          useDataspaceContextStore.getState().currentContext.orgId ?? null;
        if (currentDataspaceId === null) {
          currentDataspaceId = await waitForDataspaceContext(5000);
        }

        // Re-reads the live dataspace at every async boundary. If the user
        // switched dataspaces while this initializeSpace was running, the
        // captured `currentDataspaceId` no longer matches and we must abandon
        // any pending state mutations to avoid leaking previous-org entities
        // into the now-active org's cache.
        const liveDataspaceId = (): string | null =>
          useDataspaceContextStore.getState().currentContext.orgId ?? null;
        const isStale = (): boolean => liveDataspaceId() !== currentDataspaceId;

        // Stage 0 — paint from IDB if a fresh-enough snapshot exists for the
        // current dataspace. The REST refresh below is fire-and-forget unless
        // the cache is missing/stale, in which case we await it.
        const snapshot = await readSnapshot(spaceId);
        if (isStale()) {
          log.info(
            `[SpaceEntityStore] initializeSpace=${spaceId} discarded IDB read — dataspace switched mid-fetch`,
          );
          return;
        }
        const cacheUsable =
          !!snapshot &&
          snapshot.meta.dataspaceId === currentDataspaceId &&
          Date.now() - snapshot.meta.fetchedAt <= STALE_AFTER_MS;

        if (cacheUsable && snapshot) {
          const idbEntry = buildEntry(spaceId, snapshot.entities, 'idb');
          set((s) => ({
            caches: lruInsert(s.caches, idbEntry),
            bumpVersion: s.bumpVersion + 1,
          }));
          log.info(
            `[SpaceEntityStore] 🪟 initializeSpace=${spaceId} idbHit count=${snapshot.entities.length} fetchedAt=${snapshot.meta.fetchedAt}`,
          );
        }

        const refreshPromise = (async () => {
          // Wait for the org Y.Doc to sync before reading edges. The two-stage
          // fetch in entityService.getEntitiesForSpace short-circuits Stage 2
          // when `edges` is empty (entityService.ts:886), so without this wait
          // a hard reload races the Y.Doc handshake: initializeSpace runs
          // before the WS room cold-seeds edges from `dataspace_edges`,
          // readEdgesSnapshot returns [], Stage 2 is skipped, and any space
          // member attached only via an edge (e.g. images persisted by
          // /image-studio/persist-variant, which leave entities.spaceId=null
          // and rely on the dataspace edge for hierarchy) silently disappears.
          // The empty REST result then overwrites the IDB-painted entry and
          // the IDB snapshot itself, so the missing entities also don't
          // recover on the next refresh.
          //
          // Cold reloads can also trigger a destroy/reinit of the org Y.Doc
          // mid-boot: App.tsx fetches /user/context asynchronously, so the
          // first subscriber to initOrgYDocStore can land on a placeholder
          // dataspace (the user.sub) before the real org id arrives. When
          // initEntitySystemYjsStore is called again for the correct org,
          // destroyOrgYDocStore rejects every pending waitForOrgSync with the
          // "Destroyed before sync completed" sentinel. That rejection is
          // transient — the next sync against the freshly initialized store
          // will succeed — so we re-await rather than fall through with
          // empty edges. We bound the retry loop by an absolute deadline so
          // a pathological destroy/reinit cycle can't suspend the refresh
          // forever; on timeout we degrade gracefully (readEdgesSnapshot
          // also falls back to dataspaceTreeStore.nodesById, which has its
          // own REST fallback to GET /api/dataspaces/:id/edges).
          const ORG_SYNC_BUDGET_MS = 8000;
          const orgSyncDeadline = Date.now() + ORG_SYNC_BUDGET_MS;
          while (Date.now() < orgSyncDeadline) {
            const remaining = Math.max(0, orgSyncDeadline - Date.now());
            try {
              await waitForOrgSync(remaining);
              break;
            } catch (err) {
              if (isStale()) return;
              const transient = String(err).includes('Destroyed');
              if (!transient) {
                log.warn(
                  `[SpaceEntityStore] initializeSpace=${spaceId} Y.Doc sync timed out — proceeding with whatever edges readEdgesSnapshot can supply`,
                );
                break;
              }
              // Transient destroy/reinit — loop and re-await against the
              // newly initialized store. Yield a microtask so the caller
              // that triggered the destroy (e.g. initOrgYDocStore for the
              // real org id) has a chance to flip _currentDataspaceId
              // before our next waitForOrgSync samples _isReady.
              await Promise.resolve();
            }
          }
          if (isStale()) {
            log.info(
              `[SpaceEntityStore] initializeSpace=${spaceId} discarded post-sync — dataspace switched mid-fetch`,
            );
            return;
          }
          const edges = readEdgesSnapshot();
          const entities = await entityService.getEntitiesForSpace(spaceId, edges);
          // Cross-org guard: if the user switched dataspaces while this REST
          // call was in flight, drop the result on the floor. Otherwise
          // entities from the previous org would land in the new org's caches
          // (and IDB) and surface as stale content until LRU eviction.
          if (isStale()) {
            log.info(
              `[SpaceEntityStore] initializeSpace=${spaceId} discarded REST result — dataspace switched mid-fetch`,
            );
            return;
          }
          const entry = buildEntry(spaceId, entities, 'rest');
          set((s) => ({
            caches: lruInsert(s.caches, entry),
            bumpVersion: s.bumpVersion + 1,
          }));
          await writeSnapshot(spaceId, entities, currentDataspaceId);
          log.info(
            `[SpaceEntityStore] ✅ initializeSpace=${spaceId} count=${entities.length} ms=${(performance.now() - t0).toFixed(0)} source=rest`,
          );
        })();

        if (!cacheUsable) {
          await refreshPromise;
        } else {
          // Background refresh — never throw out of stale-while-revalidate.
          refreshPromise.catch((err) =>
            log.warn(`[SpaceEntityStore] background refresh for ${spaceId} failed`, err),
          );
        }
      } catch (err) {
        log.error(`[SpaceEntityStore] ❌ initializeSpace=${spaceId} failed`, err);
        throw err;
      } finally {
        set((s) => {
          const loading = new Set(s.loading);
          loading.delete(spaceId);
          const inflight = new Map(s.inflight);
          inflight.delete(spaceId);
          return { loading, inflight };
        });
      }
    })();

    set((s) => {
      const inflight = new Map(s.inflight);
      inflight.set(spaceId, promise);
      return { inflight };
    });

    return promise;
  },

  getEntities: (spaceId: string) => {
    const entry = get().caches.get(spaceId);
    return entry?.entities ?? [];
  },

  getEntity: (spaceId: string, entityId: string) => {
    return get().caches.get(spaceId)?.entityById.get(entityId);
  },

  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => {
    const entry = get().caches.get(spaceId);
    if (!entry) return;
    const existing = entry.entityById.get(entityId);
    if (!existing) return;
    const updated: BaseEntityType = { ...existing, ...patch };
    const nextById = new Map(entry.entityById);
    nextById.set(entityId, updated);
    if (updated.id && updated.id !== entityId) nextById.set(updated.id, updated);
    const nextList = entry.entities.map((e) =>
      e._id === entityId || e.id === entityId ? updated : e,
    );
    const nextEntry: SpaceCacheEntry = {
      ...entry,
      entities: nextList,
      entityById: nextById,
    };
    set((s) => ({
      caches: lruInsert(s.caches, nextEntry),
      bumpVersion: s.bumpVersion + 1,
    }));
  },

  evictSpace: (spaceId: string) => {
    set((s) => {
      const next = new Map(s.caches);
      next.delete(spaceId);
      return { caches: next, bumpVersion: s.bumpVersion + 1 };
    });
  },

  clearAll: () => {
    set({ caches: new Map(), bumpVersion: 0, loading: new Set(), inflight: new Map() });
  },
}));

// Stable reference for the empty-space fallback.
// Zustand's useSyncExternalStore compares snapshots with Object.is, so
// returning a new [] literal on every call when spaceId is null/undefined
// would make every render look "changed", causing an infinite re-render loop.
const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * React hook — returns the cached entities for a space.
 * Callers should ensure initializeSpace has been kicked off (e.g. in
 * SpacePageWrapper). This hook intentionally does NOT auto-fetch to keep
 * render trees deterministic and avoid duplicate inflight requests.
 */
export function useSpaceEntities(spaceId: string | null | undefined): BaseEntityType[] {
  return useSpaceEntityStore((s) =>
    spaceId ? (s.caches.get(spaceId)?.entities ?? EMPTY_ENTITIES) : EMPTY_ENTITIES,
  );
}

export function useSpaceEntity(
  spaceId: string | null | undefined,
  entityId: string | null | undefined,
): BaseEntityType | undefined {
  return useSpaceEntityStore((s) =>
    spaceId && entityId ? s.caches.get(spaceId)?.entityById.get(entityId) : undefined,
  );
}

/**
 * Apply an upsert from the registry's live-update bus into every cache that
 * already holds the entity, plus the entity's own space cache when present.
 *
 * Cross-space references: a single entity can appear in multiple cached
 * spaces (e.g. a Doc linked from Space A's tree but living in Space B's
 * spaceId). We update every cache that has it; new "add" actions are only
 * placed in the cache for the entity's own spaceId.
 */
function _applyUpsertToCaches(entity: BaseEntityType): void {
  const entityId = entity._id || (entity as any).id;
  if (!entityId) return;
  const state = useSpaceEntityStore.getState();
  const incomingSpaceId: string | null = (entity as any).spaceId ?? null;
  let mutated = false;
  const nextCaches = new Map(state.caches);

  for (const [spaceId, entry] of nextCaches) {
    const existing = entry.entityById.get(entityId);
    const ownsThisSpace = spaceId === incomingSpaceId;
    if (!existing && !ownsThisSpace) continue;

    const merged: BaseEntityType = existing
      ? ({ ...existing, ...entity, skeleton: { ...((existing as any).skeleton ?? {}), ...((entity as any).skeleton ?? {}) } } as BaseEntityType)
      : (entity as BaseEntityType);

    const nextById = new Map(entry.entityById);
    nextById.set(entityId, merged);
    if ((merged as any).id && (merged as any).id !== entityId) {
      nextById.set((merged as any).id, merged);
    }
    const nextList = existing
      ? entry.entities.map((e) => (e._id === entityId || (e as any).id === entityId ? merged : e))
      : [...entry.entities, merged];
    nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
    idbPatchEntity(spaceId, merged).catch(() => {});
    mutated = true;
  }

  if (mutated) {
    useSpaceEntityStore.setState((s) => ({
      caches: nextCaches,
      bumpVersion: s.bumpVersion + 1,
    }));
  }
}

function _applyDeleteToCaches(entityId: string): void {
  const state = useSpaceEntityStore.getState();
  let mutated = false;
  const nextCaches = new Map(state.caches);
  for (const [spaceId, entry] of nextCaches) {
    if (!entry.entityById.has(entityId)) continue;
    const nextById = new Map(entry.entityById);
    nextById.delete(entityId);
    const nextList = entry.entities.filter(
      (e) => e._id !== entityId && (e as any).id !== entityId,
    );
    nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
    mutated = true;
  }
  if (mutated) {
    useSpaceEntityStore.setState((s) => ({
      caches: nextCaches,
      bumpVersion: s.bumpVersion + 1,
    }));
  }
}

function _getByIdAcrossCaches(id: string): BaseEntityType | undefined {
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    const e = entry.entityById.get(id);
    if (e) return e;
  }
  return undefined;
}

function _getByTypeAcrossCaches(entityType: string): BaseEntityType[] {
  const out: BaseEntityType[] = [];
  const seen = new Set<string>();
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    for (const e of entry.entities) {
      if (e.entityType !== entityType) continue;
      const id = e._id || (e as any).id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  return out;
}

function _dumpAllSpaceEntities(): BaseEntityType[] {
  const out: BaseEntityType[] = [];
  const seen = new Set<string>();
  for (const entry of useSpaceEntityStore.getState().caches.values()) {
    for (const e of entry.entities) {
      const id = e._id || (e as any).id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  return out;
}

export async function dropSpaceEntityCache(spaceId: string): Promise<void> {
  useSpaceEntityStore.getState().evictSpace(spaceId);
  await deleteSnapshot(spaceId);
}

/**
 * Live-update wiring is now driven by the entity registry (see the
 * `registerScopeAdapter('space', ...)` call below). The orgYDocStore live-
 * update bus calls `entityRegistry.upsert / deleteEntity` which dispatches
 * straight into our adapter; no separate subscription handoff is needed.
 *
 * This export is retained as an idempotent no-op so existing callers (App.tsx,
 * entitySystemYjsStore.initEntitySystemYjsStore) don't need to be changed in
 * the same commit as the registry rollout. It has no behavior.
 *
 * The adapter is registered when this module is first imported. That happens
 * during the React boot path because the router and most consumers transitively
 * import spaceEntityStore — guaranteeing the dispatcher is wired before the
 * org Y.Doc starts emitting updates.
 */
export function installSpaceEntityLiveUpdates(): void {
  // intentionally empty — registration happens at module load via
  // registerScopeAdapter('space', ...) below.
}

// Register the adapter with the entity registry so the live-update bus
// (orgYDocStore) and registry dispatchers route into us.
//
// Note: this adapter intentionally accepts entities of any type the registry
// dispatches to it. The registry has the authoritative say on routing; if it
// resolved an entity (e.g. Automation, Tasks.Task, BankTransaction) to the
// space adapter via the adapter-missing fallback, we must honor that and
// place the entity in the matching space cache.
registerScopeAdapter('space', {
  getById: _getByIdAcrossCaches,
  getByType: _getByTypeAcrossCaches,
  upsert: _applyUpsertToCaches,
  deleteEntity: _applyDeleteToCaches,
  dumpAll: _dumpAllSpaceEntities,
});

// Bridge bumpVersion ticks → entityRegistry.subscribeToChanges so app stores
// (mail / tasks / bank / …) can listen for "any entity changed" without
// importing the legacy `useEntityStore` shim.
useSpaceEntityStore.subscribe((state, prev) => {
  if (state.bumpVersion === prev.bumpVersion) return;
  notifyRegistryChange();
});
